import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { MdClose } from 'react-icons/md';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FieldArray } from 'formik';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import _ from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import axios from 'axios';
import queryString from 'query-string';
import { cookie } from 'utils/cookies/cookies';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_WHATSNEW, UPDATE_WHATSNEW } from 'actions/whatsNew';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';

const TEMPLATE_OPTIONS = [
  { label: 'Template 1 — Simple banner', value: 1 },
  { label: 'Template 2 — Feature highlights', value: 2 },
  { label: 'Template 3 — Ordered / step', value: 3 },
];

// Per-slide audience targeting (CDB-300)
const TIER_OPTIONS = [
  { label: 'Bronze', value: 'Bronze' },
  { label: 'Silver', value: 'Silver' },
  { label: 'Gold', value: 'Gold' },
  { label: 'Platinum', value: 'Platinum' },
  { label: 'Red', value: 'Red' },
];
const CUSTOMER_TYPE_OPTIONS = [
  { label: 'Customer', value: 'customer' },
  { label: 'Fleet Member', value: 'fleet_member' },
  { label: 'Fleet Owner', value: 'fleet_owner' },
];

const emptySlide = { template: 1, title: '', description: '', image_url: '', bullets: [], cta: { text: '', deep_link: '' }, duration_seconds: '' };

const WhatsNewForm = (props) => {
  const { isEdit, onClose = _.noop() } = props;
  const dispatch = useDispatch();

  const detail = useSelector((state) => state.whatsNew.whatsNewDetail);
  const isLoading = useSelector((state) => state.whatsNew.isLoading);

  // S3 presigned-URL upload (same pattern as FirmWareUpdate)
  const uploadImage = useCallback((acceptedFiles, path, setFieldValue) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (!file) return;
    const token = cookie.get('token');
    axios({
      method: 'get',
      headers: { Authorization: `Bearer ${token}` },
      url: `${process.env.REACT_APP_API_URL}/util/fileupload?name=${file.name}`,
    }).then((res) => {
      const signedRequest = _.get(res, 'data.signedRequest', '');
      if (!signedRequest) return;
      axios({ method: 'put', headers: { 'Content-Type': file.type }, url: signedRequest, data: file }).then((putRes) => {
        if (putRes.status === 200) {
          const imgUrlData = queryString.parseUrl(signedRequest);
          setFieldValue(path, imgUrlData.url);
        }
      });
    });
  }, []);

  const create = useCallback((data) => {
    dispatch({
      type: CREATE_WHATSNEW.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) onClose();
      },
    });
  }, []);

  const update = useCallback((data) => {
    dispatch({
      type: UPDATE_WHATSNEW.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE) onClose();
      },
    });
  }, []);

  const schema = Yup.object().shape({
    release_version: Yup.string().required('Release version is required').strict(true).trim('No leading/trailing spaces'),
    slides: Yup.array()
      .of(
        Yup.object().shape({
          title: Yup.string().trim().required('Slide title is required'),
          duration_seconds: Yup.number()
            .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
            .min(0, 'Duration must be between 0 and 60 sec')
            .max(60, 'Duration must be between 0 and 60 sec')
            .nullable(),
        }),
      )
      .min(1, 'Add at least one slide'),
  });

  const initialValues = isEdit
    ? {
        ...detail,
        start_date: detail.start_date ? moment(detail.start_date).format('YYYY-MM-DD') : '',
        end_date: detail.end_date ? moment(detail.end_date).format('YYYY-MM-DD') : '',
        slides: _.get(detail, 'slides', []).map((s) => ({
          ...s,
          bullets: _.get(s, 'bullets', []),
          targeting: s.targeting
            ? {
                ...s.targeting,
                last_session_from: s.targeting.last_session_from ? moment(s.targeting.last_session_from).format('YYYY-MM-DD') : '',
                last_session_to: s.targeting.last_session_to ? moment(s.targeting.last_session_to).format('YYYY-MM-DD') : '',
                created_from: s.targeting.created_from ? moment(s.targeting.created_from).format('YYYY-MM-DD') : '',
                created_to: s.targeting.created_to ? moment(s.targeting.created_to).format('YYYY-MM-DD') : '',
              }
            : undefined,
        })),
      }
    : {
        release_version: '',
        is_active: false,
        start_date: '',
        end_date: '',
        frequency: 'ONCE',
        ring: { label: "What's New", image_url: '' },
        slides: [{ ...emptySlide }],
      };

  // build a slide's targeting object; returns undefined when nothing is set so
  // the slide stays "shown to everyone" (CDB-300)
  const buildTargeting = (tg) => {
    if (!tg) return undefined;
    const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const dat = (v) => (v || null);
    const arr = (v) => (Array.isArray(v) && v.length ? v : undefined);
    const out = {
      tiers: arr(tg.tiers),
      customer_types: arr(tg.customer_types),
      wallet_min: num(tg.wallet_min),
      wallet_max: num(tg.wallet_max),
      last_session_from: dat(tg.last_session_from),
      last_session_to: dat(tg.last_session_to),
      created_from: dat(tg.created_from),
      created_to: dat(tg.created_to),
    };
    const hasAny =
      out.tiers ||
      out.customer_types ||
      out.wallet_min != null ||
      out.wallet_max != null ||
      out.last_session_from ||
      out.last_session_to ||
      out.created_from ||
      out.created_to;
    return hasAny ? out : undefined;
  };

  // normalise before submit: drop empty strings -> sensible types, add step index for ordered bullets
  const buildPayload = (values) => {
    const payload = _.cloneDeep(values);
    payload.slides = (payload.slides || []).map((slide, idx) => ({
      ...slide,
      order: idx + 1,
      duration_seconds: slide.duration_seconds === '' ? null : Number(slide.duration_seconds),
      targeting: buildTargeting(slide.targeting),
      // Template 1 is the only template with an image — drop it for 2/3 so the
      // CMS matches what the app renders.
      image_url: Number(slide.template) === 1 ? slide.image_url || null : null,
      // Template 1 (simple banner) has no bullets — drop them even if the admin
      // added some before switching template, so CMS matches what the app shows.
      bullets:
        Number(slide.template) === 1
          ? []
          : (slide.bullets || [])
              .filter((b) => _.trim(_.get(b, 'text', '')))
              .map((b, bIdx) => ({ text: b.text, icon: b.icon || null, step: Number(slide.template) === 3 ? bIdx + 1 : null })),
    }));
    if (!payload.start_date) delete payload.start_date;
    if (!payload.end_date) delete payload.end_date;
    return payload;
  };

  return (
    <div className="faq-form-page__main">
      <Card>
        <div className="faq-form-header__block">
          <div className="faq-header-name">
            <span>{isEdit ? 'Edit' : 'Add'} What&apos;s New</span>
          </div>
          <div className="close___btn">
            <MdClose size={30} color="#be210b" onClick={onClose} />
          </div>
        </div>
        <div className="faq-form-body__block">
          <Formik
            enableReinitialize={!!isEdit}
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
              const payload = buildPayload(values);
              if (isEdit) update(payload);
              else create(payload);
              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                {/* ---- Release-level config ---- */}
                <Row>
                  <Col lg={4} md={6}>
                    <TextInput
                      isRequired
                      label="Release Version"
                      placeholder="e.g. 2.2.3"
                      name="release_version"
                      value={_.get(values, 'release_version', '')}
                      onChange={handleChange}
                      error={errors.release_version && touched.release_version ? errors.release_version : null}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <TextInput
                      label="Start Date"
                      type="date"
                      name="start_date"
                      value={_.get(values, 'start_date', '')}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <TextInput
                      label="End Date"
                      type="date"
                      name="end_date"
                      value={_.get(values, 'end_date', '')}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <TextInput
                      label="Ring Label"
                      placeholder="What's New"
                      name="ring.label"
                      value={_.get(values, 'ring.label', '')}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <div className="form-label" style={{ marginTop: 16, fontWeight: 600 }}>Slides</div>

                {/* ---- Slides ---- */}
                <FieldArray name="slides">
                  {({ push, remove }) => (
                    <div className="whatsNew-slides">
                      {_.map(_.get(values, 'slides', []), (slide, index) => (
                        <Card key={`slide-${index}`} className="mb-3" style={{ padding: 12, marginTop: 12 }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>Slide {index + 1}</strong>
                            {values.slides.length > 1 && (
                              <MdClose size={22} color="#be210b" style={{ cursor: 'pointer' }} onClick={() => remove(index)} />
                            )}
                          </div>
                          <Row>
                            <Col lg={4} md={6}>
                              <Select
                                label="Template"
                                options={TEMPLATE_OPTIONS}
                                value={_.get(slide, 'template', 1)}
                                onChange={(val) => setFieldValue(`slides[${index}].template`, val)}
                              />
                            </Col>
                            <Col lg={4} md={6}>
                              <TextInput
                                isRequired
                                label="Title"
                                name={`slides[${index}].title`}
                                value={_.get(slide, 'title', '')}
                                onChange={handleChange}
                                error={
                                  _.get(errors, `slides[${index}].title`) && _.get(touched, `slides[${index}].title`)
                                    ? _.get(errors, `slides[${index}].title`)
                                    : null
                                }
                              />
                            </Col>
                            <Col lg={4} md={6}>
                              <TextInput
                                label="Slide Duration (sec, 0–60, optional)"
                                type="number"
                                min={0}
                                max={60}
                                name={`slides[${index}].duration_seconds`}
                                value={_.get(slide, 'duration_seconds', '')}
                                onChange={handleChange}
                                error={
                                  _.get(errors, `slides[${index}].duration_seconds`) && _.get(touched, `slides[${index}].duration_seconds`)
                                    ? _.get(errors, `slides[${index}].duration_seconds`)
                                    : null
                                }
                              />
                            </Col>
                            <Col lg={12}>
                              <TextInput
                                as="textarea"
                                rows="3"
                                label="Description"
                                name={`slides[${index}].description`}
                                value={_.get(slide, 'description', '')}
                                onChange={handleChange}
                              />
                            </Col>
                            {/* image — Template 1 only (templates 2 & 3 have no image in the app) */}
                            {Number(_.get(slide, 'template')) === 1 && (
                              <Col lg={12}>
                                <label className="form-label" htmlFor={`slide-image-${index}`}>Image (optional)</label>
                                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                  <input
                                    id={`slide-image-${index}`}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => uploadImage(e.target.files, `slides[${index}].image_url`, setFieldValue)}
                                  />
                                  {_.get(slide, 'image_url') && (
                                    <img src={_.get(slide, 'image_url')} alt="slide" style={{ height: 40, borderRadius: 4 }} />
                                  )}
                                </div>
                              </Col>
                            )}
                          </Row>

                          {/* bullets — template 2 & 3 only; template 1 (simple banner) has none */}
                          {[2, 3].includes(Number(_.get(slide, 'template'))) && (
                          <FieldArray name={`slides[${index}].bullets`}>
                            {({ push: pushBullet, remove: removeBullet }) => (
                              <div className="whatsNew-bullets" style={{ marginTop: 8 }}>
                                <div className="form-label">Bullet points (template 2 / 3)</div>
                                {_.map(_.get(slide, 'bullets', []), (bullet, bIndex) => (
                                  <Row key={`bullet-${index}-${bIndex}`} className="align-items-center">
                                    <Col lg={10} md={9}>
                                      <TextInput
                                        placeholder={`Point ${bIndex + 1}`}
                                        name={`slides[${index}].bullets[${bIndex}].text`}
                                        value={_.get(bullet, 'text', '')}
                                        onChange={handleChange}
                                      />
                                    </Col>
                                    <Col lg={2} md={3}>
                                      <MdClose size={20} color="#be210b" style={{ cursor: 'pointer' }} onClick={() => removeBullet(bIndex)} />
                                    </Col>
                                  </Row>
                                ))}
                                <Button type="button" className="form_btn" onClick={() => pushBullet({ text: '', icon: '' })}>
                                  + Add bullet
                                </Button>
                              </div>
                            )}
                          </FieldArray>
                          )}

                          {/* audience targeting — optional; blank = shown to everyone (CDB-300) */}
                          <div className="whatsNew-targeting" style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 10 }}>
                            <div className="form-label" style={{ fontWeight: 600 }}>Audience (optional)</div>
                            <small style={{ color: '#888' }}>Leave everything blank to show this slide to all users. Any filter you set must all match (AND).</small>
                            <Row>
                              <Col lg={6} md={6}>
                                <Select
                                  label="Charge Club Tier"
                                  isMulti
                                  isClearable
                                  options={TIER_OPTIONS}
                                  placeholder="Any tier"
                                  value={_.get(slide, 'targeting.tiers', [])}
                                  onChange={(val) => setFieldValue(`slides[${index}].targeting.tiers`, val || [])}
                                />
                              </Col>
                              <Col lg={6} md={6}>
                                <Select
                                  label="Customer Type"
                                  isMulti
                                  isClearable
                                  options={CUSTOMER_TYPE_OPTIONS}
                                  placeholder="Any customer type"
                                  value={_.get(slide, 'targeting.customer_types', [])}
                                  onChange={(val) => setFieldValue(`slides[${index}].targeting.customer_types`, val || [])}
                                />
                              </Col>
                              <Col lg={3} md={6}>
                                <TextInput
                                  label="Wallet Balance Min"
                                  type="number"
                                  name={`slides[${index}].targeting.wallet_min`}
                                  value={_.get(slide, 'targeting.wallet_min', '')}
                                  onChange={handleChange}
                                />
                              </Col>
                              <Col lg={3} md={6}>
                                <TextInput
                                  label="Wallet Balance Max"
                                  type="number"
                                  name={`slides[${index}].targeting.wallet_max`}
                                  value={_.get(slide, 'targeting.wallet_max', '')}
                                  onChange={handleChange}
                                />
                              </Col>
                              <Col lg={3} md={6}>
                                <TextInput
                                  label="Last Session From"
                                  type="date"
                                  name={`slides[${index}].targeting.last_session_from`}
                                  value={_.get(slide, 'targeting.last_session_from', '')}
                                  onChange={handleChange}
                                />
                              </Col>
                              <Col lg={3} md={6}>
                                <TextInput
                                  label="Last Session To"
                                  type="date"
                                  name={`slides[${index}].targeting.last_session_to`}
                                  value={_.get(slide, 'targeting.last_session_to', '')}
                                  onChange={handleChange}
                                />
                              </Col>
                              <Col lg={3} md={6}>
                                <TextInput
                                  label="Account Created From"
                                  type="date"
                                  name={`slides[${index}].targeting.created_from`}
                                  value={_.get(slide, 'targeting.created_from', '')}
                                  onChange={handleChange}
                                />
                              </Col>
                              <Col lg={3} md={6}>
                                <TextInput
                                  label="Account Created To"
                                  type="date"
                                  name={`slides[${index}].targeting.created_to`}
                                  value={_.get(slide, 'targeting.created_to', '')}
                                  onChange={handleChange}
                                />
                              </Col>
                            </Row>
                          </div>
                        </Card>
                      ))}
                      <Button type="button" className="form_btn" onClick={() => push({ ...emptySlide })}>
                        + Add slide
                      </Button>
                    </div>
                  )}
                </FieldArray>

                <div className="form-btn__block" style={{ marginTop: 16 }}>
                  <Button className="form_btn cancel____btn" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting || isLoading} className="form_btn">
                    {isEdit ? 'Update' : 'Add'} What&apos;s New
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

WhatsNewForm.propTypes = {
  onClose: PropTypes.func,
  isEdit: PropTypes.bool,
};

export default WhatsNewForm;
